import React from 'react';
import { EraserIcon, PaintbrushIcon, ShieldIcon } from 'lucide-react';
import { PanelShell } from '../../ui/PanelShell';
import { PillButton } from '../../ui/PillButton';
import { NumberField } from '../../ui/NumberField';
import { FillSlider } from '../../ui/FillSlider';
import { InfoTip } from '../../ui/InfoTip';
import { ProtectIcon } from '../../icons/ToolIcons';
import { AiProtectPrompt } from './AiProtectPrompt';
import type { ProtectAiResult, ProtectMode, ProtectSettings, ProtectTool } from '../../../types/protect';
type ProtectPanelProps = {
  settings: ProtectSettings;
  onSettingsChange: (settings: ProtectSettings) => void;
  onClose: () => void;
  onDone: () => void;
  onDeselectAll: () => void;
  onRunAi: () => void;
  aiResult: ProtectAiResult | null;
};
const tools: {
  id: ProtectTool;
  label: string;
  Icon: typeof PaintbrushIcon;
}[] = [{
  id: 'paint',
  label: 'Paint',
  Icon: PaintbrushIcon
}, {
  id: 'erase',
  label: 'Erase',
  Icon: EraserIcon
}];
const modes: {
  id: ProtectMode;
  label: string;
}[] = [{
  id: 'protected',
  label: 'Protected Surface'
}, {
  id: 'soft',
  label: 'Soft Preference'
}];
export function ProtectPanel({
  settings,
  onSettingsChange,
  onClose,
  onDone,
  onDeselectAll,
  onRunAi,
  aiResult
}: ProtectPanelProps) {
  const update = (patch: Partial<ProtectSettings>) => onSettingsChange({
    ...settings,
    ...patch
  });
  return <PanelShell title="Protect Surfaces" onClose={onClose} footer={<>
          <PillButton className="h-[28px] w-[112px] flex-none" onClick={onDeselectAll} data-id="element-469" data-mp="7h9ifgw9nx7h">
            Deselect All
          </PillButton>
          <PillButton variant="primary" className="ml-auto h-[28px] w-[96px] flex-none" onClick={onDone} data-id="element-470" data-mp="ggmk3ne5fhhn">
            Done
          </PillButton>
        </>} data-id="element-468" data-mp="v7d8smkg20gj">
      
      <div className="flex items-start gap-2 pt-[9px]" data-id="element-471" data-mp="cgvrrf8z4tb0">
        <p className="flex-1 text-[11.5px] leading-snug text-pf-muted" data-id="element-472" data-mp="1rtyn5w233w2r">
          Select or paint the areas you want to protect from supports. You can also describe the areas in natural language and let AI select them for you.
        </p>
        <InfoTip label="About protected surfaces" data-id="element-473" data-mp="kl4y8lbry1w0" />
      </div>

      <div role="radiogroup" aria-label="Brush tool" className="mt-[9px] grid grid-cols-2 rounded-[7px] border border-pf-line bg-white p-[2px]" data-id="element-474" data-mp="pgak5k56fgk3">
        {tools.map(({
        id,
        label,
        Icon
      }) => {
        const active = settings.tool === id;
        return <button key={id} type="button" role="radio" aria-checked={active} onClick={() => update({
          tool: id
        })} className={`flex h-[28px] items-center justify-center gap-[8px] rounded-[5px] text-[12.5px] transition-colors duration-150 ${active ? 'bg-pf-blue text-white' : 'text-pf-ink hover:bg-pf-panel'}`} data-id="element-475" data-mp="1sszs9o8rkr5">
              
              <Icon className="h-[13px] w-[13px]" strokeWidth={2.2} data-id="element-476" data-mp="3f6ny7649ipi3" />
              {label}
            </button>;
      })}
      </div>

      <div className="mt-[10px] flex items-center gap-[10px]" data-id="element-477" data-mp="3bp9oye1yay58">
        <span className="w-[62px] shrink-0 text-[12px] text-pf-ink" data-id="element-478" data-mp="3boqa567lkpvf">Brush Size</span>
        <FillSlider ariaLabel="Brush size slider" value={settings.brushSize} min={1} max={12} step={0.25} onChange={v => update({
        brushSize: v
      })} data-id="element-479" data-mp="36rgjxocdwa6j" />
        <NumberField ariaLabel="Brush size" value={settings.brushSize} onChange={v => update({
        brushSize: v
      })} min={1} max={12} step={0.25} unit="mm" className="h-[28px] w-[84px]" data-id="element-480" data-mp="2xph2iuxgj432" />
        
      </div>

      <p className="pb-[6px] pt-[11px] text-[12px] text-pf-ink" data-id="element-481" data-mp="2nxujwdisi2qa">Selection Mode</p>
      <div role="radiogroup" aria-label="Selection mode" className="grid grid-cols-2 gap-[6px]" data-id="element-482" data-mp="2j0uc9y6akrx2">
        {modes.map(({
        id,
        label
      }) => {
        const active = settings.mode === id;
        return <button key={id} type="button" role="radio" aria-checked={active} onClick={() => update({
          mode: id
        })} className={`flex h-[30px] items-center justify-center gap-[5px] rounded-[6px] border px-[4px] text-[11px] transition-colors duration-150 ${active ? 'border-pf-blue-line bg-pf-blue-soft font-semibold text-pf-blue' : 'border-pf-line bg-white text-pf-ink hover:bg-[#fafafa]'}`} data-id="element-483" data-mp="2a1z4wirihbs6">
              
              {id === 'protected' ? <ShieldIcon className="h-[13px] w-[13px] shrink-0" fill={active ? 'currentColor' : 'none'} strokeWidth={2} data-id="element-484" data-mp="3mbi789dkth6n" /> : <ShieldIcon className="h-[13px] w-[13px] shrink-0" strokeWidth={2} strokeDasharray="3 2.5" fill={active ? '#bfdcfb' : 'none'} data-id="element-485" data-mp="3qfhlelbdw3wu" />}
              <span className="whitespace-nowrap" data-id="element-486" data-mp="3hcymudemsdge">{label}</span>
              <InfoTip label={id === 'protected' ? 'Supports are never placed here' : 'Supports avoid these areas when possible'} data-id="element-487" data-mp="3hfba9635rg0u" />
            </button>;
      })}
      </div>

      <div className="mt-[10px] flex gap-[10px] rounded-[7px] bg-pf-blue-soft px-[10px] py-[9px]" data-id="element-488" data-mp="1flc1oshf7cru">
        <ProtectIcon className="mt-[1px] h-[18px] w-[18px] shrink-0 text-pf-blue" data-id="element-489" data-mp="304fimwjwchzj" />
        <p className="text-[11.5px] leading-snug text-pf-ink" data-id="element-490" data-mp="2v6dxm62wu8lf">
          {settings.mode === 'protected' ? 'Supports will not be placed on protected surfaces.' : 'Supports will avoid soft-preference surfaces where possible.'}
          <span className="block text-pf-muted" data-id="element-491" data-mp="3515wrec553ib">This will be used by Auto-Orient and support generation.</span>
        </p>
      </div>

      <div className="mt-[12px] border-t border-pf-line" data-id="element-492" data-mp="3dvsx3w2y6pnp" />
      <AiProtectPrompt onRun={onRunAi} result={aiResult} data-id="element-493" data-mp="3dvarw9yb513n" />
    </PanelShell>;
}